import { Group, Stack } from "@mantine/core"
import { LangageIso6391 } from "@tfg/db"
import { MultiSelect } from "@tfg/ui"
import { z } from "zod"

import { ProductSheetLanguages } from "@tfg/types/endpoints/agec"
import { Locale } from "@tfg/types/schemas/enum"

import { queryClient } from "~/backend/queryClient"
import {
  findDisplaySettingsLanguagesQueryKey,
  useFindDisplaySettingsLanguages,
} from "~/gen/api/hooks/useFindDisplaySettingsLanguages"
import { useUpdateDisplaySettingsLanguages } from "~/gen/api/hooks/useUpdateDisplaySettingsLanguages"
import { PageComponentsEditor } from "~/pages/Brand/OrganizationSettings/ProductPages/ProductSheetComponents/PageComponentsEditor"
import { findLanguageFlag } from "~/utils/languages"
import { showAxiosError } from "~/utils/showAxiosError"

const TextContentJSONSchema = z.object({
  text: z.string(),
  type: z.enum(["paragraph_block", "title", "hyperlink", "strong"]),
  link: z.string().optional(),
})
export type TextContentJSON = z.infer<typeof TextContentJSONSchema>

const ContentBlockSchema = z.object({
  type: z.string(),
  textContent: z.array(TextContentJSONSchema),
})
export type ContentBlock = z.infer<typeof ContentBlockSchema>
export type Content = Partial<{
  [key in LangageIso6391]: ContentBlock[]
}>

export const ProductSheetComponents = ({
  displaySettingsId,
}: {
  displaySettingsId: string
  organizationId: number
}) => {
  const {
    data: displaySettingsLanguages,
    isLoading: isDisplaySettingsLanguagesLoading,
  } = useFindDisplaySettingsLanguages(displaySettingsId)

  const { mutate: updateDisplaySettingsLanguages } =
    useUpdateDisplaySettingsLanguages()

  return (
    <Stack>
      <MultiSelect
        data={Object.values(ProductSheetLanguages).map((language) => ({
          label: language,
          value: language,
        }))}
        w={200}
        value={displaySettingsLanguages?.data.languages ?? []}
        renderOption={(value) => {
          return (
            <Group>
              {value.option.value}
              {findLanguageFlag(value.option.value as Locale)}
            </Group>
          )
        }}
        onChange={(value) => {
          updateDisplaySettingsLanguages(
            {
              displaySettingsId,
              languages: value,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey:
                    findDisplaySettingsLanguagesQueryKey(displaySettingsId),
                })
              },
              onError: (error) => {
                showAxiosError({
                  error,
                  showNotification: true,
                })
              },
            }
          )
        }}
        label="Languages"
        disabled={isDisplaySettingsLanguagesLoading}
      />
      {displaySettingsId && <PageComponentsEditor />}
    </Stack>
  )
}
